import { Card, CardContent } from "@mui/material";
import { Bar, BarChart, CartesianGrid, Cell, ComposedChart, Line, LineChart, ReferenceLine, XAxis, YAxis } from "recharts";
import { ChartContainer, ChartLegend, ChartLegendContent, ChartTooltip, ChartTooltipContent, type ChartConfig } from "../../components/ui/chart";
import { chartMoneyFormatter, compactMoney, formatCurrency } from "./formatters";

const cashFlowConfig = {
  income: { label: "Income", color: "#15803d" },
  expense: { label: "Expense", color: "#dc2626" },
  net: { label: "Net", color: "#2563eb" },
} satisfies ChartConfig;

const comparisonConfig = {
  cumulative_expense: { label: "Current", color: "#dc2626" },
  previous_cumulative_expense: { label: "Previous", color: "#64748b" },
} satisfies ChartConfig;

const savingsConfig = {
  rate: { label: "Savings rate", color: "#15803d" },
} satisfies ChartConfig;

type CashFlowChartPoint = {
  label: string;
  income: number;
  expense: number;
  net: number;
  cumulative_expense: number;
  previous_cumulative_expense: number;
};

type MonthlySavingPoint = {
  label: string;
  rate: number;
  income: number;
  expense: number;
  net: number;
};

type StatisticsTrendChartsProps = {
  cashFlowData: CashFlowChartPoint[];
  stacked: {
    names: string[];
    colors: Record<string, string>;
    data: Array<Record<string, string | number>>;
    config: ChartConfig;
  };
  monthlySavings: MonthlySavingPoint[];
  currency: string;
  periodLabel: string;
  previousLabel: string;
  monthBuckets: boolean;
};

export function StatisticsTrendCharts({ cashFlowData, stacked, monthlySavings, currency, periodLabel, previousLabel, monthBuckets }: StatisticsTrendChartsProps) {
  return (
    <>
      <Card className="records-panel stat-card stat-wide-card"><CardContent className="records-panel-content">
        <div className="stat-card-heading"><h2>Cash flow trend</h2><span>{periodLabel}</span></div>
        <ChartContainer config={cashFlowConfig} className="stat-area-chart"><ComposedChart accessibilityLayer data={cashFlowData} margin={{ left: 8, right: 8, top: 12, bottom: 0 }}>
          <CartesianGrid vertical={false} stroke="rgba(15, 23, 42, 0.08)" /><XAxis dataKey="label" tickLine={false} axisLine={false} tickMargin={10} tick={{ fontSize: 12 }} /><YAxis tickLine={false} axisLine={false} tick={{ fontSize: 12 }} tickFormatter={compactMoney} width={56} /><ChartTooltip content={<ChartTooltipContent valueFormatter={chartMoneyFormatter(currency)} />} /><ChartLegend content={<ChartLegendContent />} /><Bar dataKey="income" fill="var(--color-income)" radius={4} maxBarSize={28} /><Bar dataKey="expense" fill="var(--color-expense)" radius={4} maxBarSize={28} /><Line type="monotone" dataKey="net" stroke="var(--color-net)" strokeWidth={2} dot={monthBuckets} />
        </ComposedChart></ChartContainer>
      </CardContent></Card>

      {stacked.names.length ? <Card className="records-panel stat-card stat-wide-card"><CardContent className="records-panel-content">
        <div className="stat-card-heading"><h2>Expenses by category</h2><span>Top categories over time</span></div>
        <ChartContainer config={stacked.config} className="stat-line-chart"><BarChart accessibilityLayer data={stacked.data} margin={{ left: 8, right: 8, top: 12, bottom: 0 }}>
          <CartesianGrid vertical={false} stroke="rgba(15, 23, 42, 0.08)" /><XAxis dataKey="label" tickLine={false} axisLine={false} tickMargin={10} tick={{ fontSize: 12 }} /><YAxis tickLine={false} axisLine={false} tick={{ fontSize: 12 }} tickFormatter={compactMoney} width={56} /><ChartTooltip content={<ChartTooltipContent valueFormatter={chartMoneyFormatter(currency)} />} /><ChartLegend content={<ChartLegendContent />} />{stacked.names.map((name) => <Bar key={name} dataKey={name} stackId="categories" fill={stacked.colors[name]} maxBarSize={36} />)}
        </BarChart></ChartContainer>
      </CardContent></Card> : null}

      <Card className="records-panel stat-card stat-wide-card"><CardContent className="records-panel-content">
        <div className="stat-card-heading"><h2>Spend pace</h2><span>Compared with {previousLabel}</span></div>
        <ChartContainer config={comparisonConfig} className="stat-line-chart"><LineChart accessibilityLayer data={cashFlowData} margin={{ left: 8, right: 8, top: 12, bottom: 0 }}>
          <CartesianGrid vertical={false} stroke="rgba(15, 23, 42, 0.08)" /><XAxis dataKey="label" tickLine={false} axisLine={false} tickMargin={10} tick={{ fontSize: 12 }} /><YAxis tickLine={false} axisLine={false} tick={{ fontSize: 12 }} tickFormatter={compactMoney} width={56} /><ChartTooltip content={<ChartTooltipContent valueFormatter={chartMoneyFormatter(currency)} />} /><ChartLegend content={<ChartLegendContent />} /><Line type="monotone" dataKey="cumulative_expense" stroke="var(--color-cumulative_expense)" strokeWidth={2.5} dot={false} /><Line type="monotone" dataKey="previous_cumulative_expense" stroke="var(--color-previous_cumulative_expense)" strokeWidth={2} strokeDasharray="5 5" dot={false} />
        </LineChart></ChartContainer>
      </CardContent></Card>

      {monthlySavings.length >= 2 ? <Card className="records-panel stat-card stat-wide-card"><CardContent className="records-panel-content">
        <div className="stat-card-heading"><h2>Monthly savings rate</h2><span>Share of income kept each month</span></div>
        <ChartContainer config={savingsConfig} className="stat-line-chart"><BarChart accessibilityLayer data={monthlySavings} margin={{ left: 8, right: 8, top: 12, bottom: 0 }}>
          <CartesianGrid vertical={false} stroke="rgba(15, 23, 42, 0.08)" /><XAxis dataKey="label" tickLine={false} axisLine={false} tickMargin={10} tick={{ fontSize: 12 }} /><YAxis tickLine={false} axisLine={false} tick={{ fontSize: 12 }} tickFormatter={(value) => `${value}%`} width={48} /><ReferenceLine y={0} stroke="rgba(15, 23, 42, 0.3)" />
          <ChartTooltip content={({ active, payload, label }: { active?: boolean; payload?: Array<{ payload?: MonthlySavingPoint }>; label?: string | number }) => {
            const point = payload?.[0]?.payload;
            if (!active || !point) return null;
            return <div className="chart-tooltip"><strong>{label}</strong><span className="chart-tooltip-row"><i style={{ backgroundColor: point.rate >= 0 ? "#15803d" : "#dc2626" }}></i><span>Savings rate</span><b>{point.rate}%</b></span><span className="chart-tooltip-row"><i style={{ backgroundColor: "#64748b" }}></i><span>Income</span><b>{formatCurrency(point.income, currency)}</b></span><span className="chart-tooltip-row"><i style={{ backgroundColor: "#2563eb" }}></i><span>Net</span><b>{formatCurrency(point.net, currency)}</b></span></div>;
          }} />
          <Bar dataKey="rate" radius={4} maxBarSize={36}>{monthlySavings.map((entry) => <Cell key={entry.label} fill={entry.rate >= 0 ? "#15803d" : "#dc2626"} />)}</Bar>
        </BarChart></ChartContainer>
      </CardContent></Card> : null}
    </>
  );
}
